import { Shift } from './types';
import { createShiftDate } from './date-utils';

const VTIMEZONE = `BEGIN:VTIMEZONE
TZID:Australia/Melbourne
BEGIN:STANDARD
DTSTART:19700405T030000
RRULE:FREQ=YEARLY;BYDAY=1SU;BYMONTH=4
TZOFFSETFROM:+1100
TZOFFSETTO:+1000
TZNAME:AEST
END:STANDARD
BEGIN:DAYLIGHT
DTSTART:19701004T020000
RRULE:FREQ=YEARLY;BYDAY=1SU;BYMONTH=10
TZOFFSETFROM:+1000
TZOFFSETTO:+1100
TZNAME:AEDT
END:DAYLIGHT
END:VTIMEZONE`;

function formatIcsDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}T${h}${min}${s}`;
}

function formatIcsDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function nextDay(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
}

export function shiftPeriod(startTime: string): string | null {
  if (!startTime || startTime.length < 2) return null;
  const h = parseInt(startTime.slice(0, 2), 10);
  if (isNaN(h)) return null;
  if (h >= 6 && h < 12) return 'AM';
  if (h >= 12 && h < 18) return 'PM';
  if (h >= 18 || h < 6) return 'Night';
  return null;
}

export function icsSummary(shift: Shift, format: string): string {
  const site = format === 'mmc' ? 'MMC' : format === 'vhh' ? 'VHH' : format === 'casey' ? 'Casey' : '';
  if (shift.type === 'leave') return `Leave @ ${site}`;
  if (shift.type !== 'shift' || !shift.startTime) return shift.label;
  if (format === 'vhh') return `${shift.label} @ ${site}`;
  const period = shiftPeriod(shift.startTime);
  return period ? `${period} Shift @ ${site}` : `Shift @ ${site}`;
}

let exportCounter = 0;

export function generateIcs(shifts: Shift[], format: string): string {
  const events: string[] = [];
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/g, '');
  const baseUid = `rc-${Date.now()}-${exportCounter++}-`;

  for (const shift of shifts) {
    const dtStamp = now + 'Z';
    const uidVal = baseUid + `${Math.random().toString(36).slice(2, 8)}@app`;

    if (shift.type === 'shift' && shift.startTime && shift.endTime) {
      const startDate = createShiftDate(shift.date, shift.startTime);
      const endDate = createShiftDate(shift.date, shift.endTime, true, shift.startTime);

      events.push(
        `BEGIN:VEVENT`,
        `DTSTART;TZID=Australia/Melbourne:${formatIcsDateTime(startDate)}`,
        `DTEND;TZID=Australia/Melbourne:${formatIcsDateTime(endDate)}`,
        `DTSTAMP:${dtStamp}`,
        `UID:${uidVal}`,
        `SUMMARY:${icsSummary(shift, format)}`,
        ...(shift.location ? [`DESCRIPTION:${shift.location}`] : []),
        `END:VEVENT`
      );
    } else {
      const dateStr = formatIcsDateOnly(shift.date);
      const endDateStr = formatIcsDateOnly(nextDay(shift.date));
      events.push(
        `BEGIN:VEVENT`,
        `DTSTART;VALUE=DATE:${dateStr}`,
        `DTEND;VALUE=DATE:${endDateStr}`,
        `DTSTAMP:${dtStamp}`,
        `UID:${uidVal}`,
        `SUMMARY:${icsSummary(shift, format)}`,
        `TRANSP:TRANSPARENT`,
        `END:VEVENT`
      );
    }
  }

  return [
    `BEGIN:VCALENDAR`,
    `VERSION:2.0`,
    `PRODID:-//Roster Convert//EN`,
    `CALSCALE:GREGORIAN`,
    `METHOD:PUBLISH`,
    VTIMEZONE,
    ...events,
    `END:VCALENDAR`,
  ].join('\r\n');
}

export function downloadIcs(content: string, filename: string): void {
  const safeName = filename.replace(/[^a-z0-9._-]/gi, '_');
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
